/**
 * @file The bounded read of one report's `runs` for its archive. Consumer:
 * `useAnalyticsReportArchive`. An admin reads every recipient's runs, newest
 * scheduled first, six per current recipient, and gets that limit back as
 * `adminLimit`: a read that returns exactly it may end inside an instant.
 * Anyone else reads only their own six newest runs, through the "My runs"
 * lanes the rules accept, one per instant, so `adminLimit` is null. A
 * malformed definition with no recipient still reads six, because Firestore
 * refuses a zero limit.
 */
import type { QueryOptions } from '@/store';

import { ANALYTICS_REPORT_ARCHIVE_SIZE } from './ANALYTICS_REPORT_ARCHIVE_SIZE';
import { analyticsMyRunsQuery } from './analyticsMyRunsQuery';

const adminQuery = (
  limit: number,
): { queryOptions: QueryOptions; adminLimit: number } => ({
  queryOptions: {
    orderBy: { field: 'scheduledAt', direction: 'desc' },
    extraConstraints: [{ type: 'limit', n: limit }],
  },
  adminLimit: limit,
});

export const analyticsReportArchiveQuery = (
  viewer: { isAdmin: boolean; uid: string; canReadOrg: boolean },
  recipientCount: number,
): { queryOptions: QueryOptions; adminLimit: number | null } =>
  viewer.isAdmin
    ? adminQuery(ANALYTICS_REPORT_ARCHIVE_SIZE * Math.max(recipientCount, 1))
    : {
        queryOptions: analyticsMyRunsQuery(
          viewer.uid,
          viewer.canReadOrg,
          ANALYTICS_REPORT_ARCHIVE_SIZE,
        ),
        adminLimit: null,
      };
